import './Accordion.css';

import { FC, HTMLAttributes, useEffect, useRef } from 'react';
import { useMergeState } from 'hooks/useMergeState';
import { uniqueId } from 'lodash';
import { concatStringish } from 'utils/concatStringish';

export interface AccodionContentItem {
  id: string;
  header: (idx: number) => JSX.Element;
  body: (idx: number) => JSX.Element;
  disabled?: boolean;
  defaultOpen?: boolean;
  testId?: string;
}

interface Props
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'content'> {
  controlled?: boolean;
  content: AccodionContentItem[];
  handleItemClick?: (id: string) => void;
}

const AccordionItem: FC<{
  item: AccodionContentItem;
  expanded: boolean;
  idx: number;
  onClick: (id: string) => void;
}> = ({ item, expanded, onClick, idx }) => {
  const headerId = useRef(uniqueId('accordion_segment_header'));
  const bodyId = useRef(uniqueId('accordion_segment_body'));

  return (
    <li
      className="AccordionItem"
      onClick={() => onClick(item.id)}
      data-testid={item.testId ?? item.id}
    >
      <h3 className="AccordionItem-header">
        <button
          type="button"
          disabled={item.disabled}
          aria-expanded={`${expanded ?? item.defaultOpen}`}
          aria-controls={bodyId.current}
          id={headerId.current}
        >
          {item.header(idx)}
        </button>
      </h3>
      <div
        className={concatStringish(
          'AccordionItem-body-wrapper',
          expanded ? 'is-open' : undefined,
        )}
      >
        <div
          id={bodyId.current}
          role="region"
          aria-labelledby={headerId.current}
          className="AccordionItem-body"
          {...(expanded ? null : { hidden: true })}
          onClick={e => e.stopPropagation()}
        >
          {item.body(idx)}
        </div>
      </div>
    </li>
  );
};

function createToggleStateMap(content: AccodionContentItem[]) {
  return content.reduce(
    (sum, item) => {
      sum[item.id] = !!item.defaultOpen;
      return sum;
    },
    {} as Record<string, boolean>,
  );
}

export const Accordion: FC<Props> = ({ className, content, ...props }) => {
  const [state, mergeState] = useMergeState(createToggleStateMap(content));

  useEffect(() => {
    mergeState(createToggleStateMap(content));
  }, [content, mergeState]);

  const handleItemClick = (id: string) => {
    const item = content.find(item => item.id === id);

    if (!item || item.disabled) return;

    mergeState({
      [id]: !state[id],
    });
  };

  return (
    <div className={concatStringish('Accordion', className)} {...props}>
      <ul>
        {content.map((contentItem, idx) => (
          <AccordionItem
            key={contentItem.id}
            item={contentItem}
            expanded={state[contentItem.id] ?? contentItem.defaultOpen ?? false}
            onClick={handleItemClick}
            idx={idx}
          />
        ))}
      </ul>
    </div>
  );
};
